import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

/**
 * How many full-width strips are showing above the navigation row. The rail is
 * pinned to the viewport's top edge by default, so while any strip shows it
 * has to anchor to the page shell instead, or it paints over the strip.
 */
export const topStripCountAtom = atom(0);

const HEIDI_2_STRIPS = ['awareness', 'request-access', 'explore-later'] as const;
type Heidi2Strip = (typeof HEIDI_2_STRIPS)[number];
type StripReadiness = 'pending' | 'ineligible' | 'showing';
type StripReport = { userId: string | undefined; readiness: StripReadiness };

// Lives in the app store, not the route: dismissing or remounting a Heidi II
// strip must not start a second education journey in the same app load.
const heidi2StripReportsAtom = atom<Partial<Record<Heidi2Strip, StripReport>>>({});
const heidi2StripShownUsersAtom = atom<ReadonlySet<string>>(new Set<string>());

/** Semantic readiness, separate from the layout-only occupancy count. */
export const useReportHeidi2Strip = (
  strip: Heidi2Strip,
  userId: string | undefined,
  readiness: StripReadiness
): void => {
  const setReports = useSetAtom(heidi2StripReportsAtom);
  const setShownUsers = useSetAtom(heidi2StripShownUsersAtom);
  useEffect(() => {
    setReports((reports) => ({ ...reports, [strip]: { userId, readiness } }));
    if (readiness === 'showing' && userId) {
      setShownUsers((users) => new Set([...users, userId]));
    }
    return () => {
      setReports((reports) => ({ ...reports, [strip]: undefined }));
    };
  }, [strip, userId, readiness, setReports, setShownUsers]);
};

export const useCanShowScribeStrip = (userId: string | undefined): boolean => {
  const reports = useAtomValue(heidi2StripReportsAtom);
  const shownUsers = useAtomValue(heidi2StripShownUsersAtom);
  return (
    !!userId &&
    !shownUsers.has(userId) &&
    HEIDI_2_STRIPS.every(
      (strip) =>
        reports[strip]?.userId === userId && reports[strip]?.readiness === 'ineligible'
    )
  );
};

/** Tells the layout a strip above the navigation row is on screen. */
export const useReportTopStrip = (isShowing: boolean): void => {
  const setCount = useSetAtom(topStripCountAtom);

  useEffect(() => {
    if (!isShowing) return;
    setCount((count) => count + 1);
    return () => {
      setCount((count) => count - 1);
    };
  }, [isShowing, setCount]);
};

/** Competing full-width strips that share the single top slot above the nav. */
export type TopStripId =
  | 'clinician-trial-end'
  | 'complimentary-trial-end'
  | 'heidi-2-awareness'
  | 'heidi-2-request-access'
  | 'heidi-2-explore-later'
  | 'scribe-layout-v2-tour';

/**
 * Higher wins the single top-strip slot. Trial-end outranks Heidi 2 family,
 * which outranks the Scribe layout V2 tour. Same-session handoff: when a
 * higher claim drops, the next eligible claimer paints.
 */
export const TopStripPriority = {
  SCRIBE_LAYOUT_V2_TOUR: 10,
  HEIDI_2_FAMILY: 20,
  COMPLIMENTARY_TRIAL_END: 30,
  // Trial members never get the complimentary strip; this only orders a tie.
  CLINICIAN_TRIAL_END: 31,
} as const;

type TopStripClaim = {
  id: TopStripId;
  priority: number;
};

const topStripClaimsAtom = atom<ReadonlyMap<TopStripId, number>>(new Map());

/** Winning strip id among active claims, or null when none are eligible. */
export const currentTopStripAtom = atom<TopStripId | null>((get) => {
  const claims = get(topStripClaimsAtom);
  let winner: TopStripClaim | null = null;
  for (const [id, priority] of claims) {
    if (
      winner === null ||
      priority > winner.priority ||
      (priority === winner.priority && id < winner.id)
    ) {
      winner = { id, priority };
    }
  }
  return winner?.id ?? null;
});

/**
 * While eligible, claim the single top-strip slot. Returns true iff this id
 * is the current winner. Losers stay mounted but must not paint.
 */
export function useClaimTopStrip(
  id: TopStripId,
  priority: number,
  isEligible: boolean
): boolean {
  const setClaims = useSetAtom(topStripClaimsAtom);
  const current = useAtomValue(currentTopStripAtom);

  useEffect(() => {
    if (!isEligible) return;
    setClaims((prev) => {
      if (prev.get(id) === priority) return prev;
      const next = new Map(prev);
      next.set(id, priority);
      return next;
    });
    return () => {
      setClaims((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    };
  }, [id, isEligible, priority, setClaims]);

  return isEligible && current === id;
}
